/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react";
import styles from "./CarbonEmission.module.css";
import api from "../Api";
import Select from "react-select";
import {
    Grid,
    Typography,
    Box,
    Card,
    CardContent,
    Chip,
    Tooltip as MuiTooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    Tabs,
    Tab,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
} from "@material-ui/core";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
} from "recharts";
import { startLoader, stopLoader } from "../../../actions/loaderActions";
import { useDispatch } from 'react-redux';
import EmptyChartImage from "../../../common/images/no-report.svg";
import { Button, Menu, MenuItem } from "@material-ui/core";
import { GetApp as ExportIcon } from "@material-ui/icons";
import html2canvas from "html2canvas";
import * as XLSX from "xlsx";
import { useRef } from "react";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { useHistory } from "react-router-dom";
import {
    PieChart,
    Pie,
    Cell,
    Legend,
} from "recharts";

const getDefaultHeaders = () => ({
    "x-tenant-schema": "dbo",
    "x-user-id": localStorage.getItem("UserId") || "",
    "x-user-email": localStorage.getItem("userEmail") || "",
});

const CarbonEmission = () => {
    const dispatch = useDispatch();
    // Summary counts
    const [summary, setSummary] = useState([]);
    const [topPerformers, setTopPerformers] = useState([]);
    // Row1 filters state
    const [companies, setCompanies] = useState([]);
    const [entities, setEntities] = useState([]);
    const [operatingUnits, setOperatingUnits] = useState([]);
    const [selectedUploadLevel, setSelectedUploadLevel] = useState("company");
    const [selectedCompanyId, setSelectedCompanyId] = useState("");
    const [selectedEntityId, setSelectedEntityId] = useState("");
    const [selectedUnitId, setSelectedUnitId] = useState("");

    // Row2 filters state
    const [categoryList, setCategoryList] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [years, setYears] = useState([{ label: "FY2025", value: "FY2025" }]);

    // Chart & performers state
    const [chartData, setChartData] = useState([]);
    const [unitLabel, setUnitLabel] = useState("");

    const [subtypes, setSubtypes] = useState([]);
    const [selectedSubtype, setSelectedSubtype] = useState(null);
    const [showEFModal, setShowEFModal] = useState(false);
    const [efTab, setEfTab] = useState(0);
    const [efData, setEfData] = useState({ fuel: [], electricity: [] });

    const [rawOperatingUnits, setRawOperatingUnits] = useState([]);


    const chartRef = useRef();
    const [anchorEl, setAnchorEl] = useState(null);
    const history = useHistory();
    const userId = localStorage.getItem("UserId");
    const pieColors = [
        "#001f6b", // Dark Navy
        "#1f77d0", // Blue
        "#764ba2", // Purple
        "#3f51b5", // Indigo
        "#4db6ac", // Teal
        "#00796b", // Deep Teal
        "#00bfa5", // Light Greenish Teal
        "#69f0ae", // Mint Green
        "#00e5ff"  // Cyan
    ];

    const barColors = [
        "#001f6b", // 2024
        "#1f77d0", // 2025
        "#764ba2", // 2026 (if added later)
        "#3f51b5", // etc.
    ];


    const [pieChartSummary, setPieChartSummary] = useState([]);
    const [unitConsumptionSummary, setunitConsumptionSummary] = useState([]);


    // Fetch companies/entities/units once
    useEffect(() => {
        async function fetchMeta() {
            dispatch(startLoader());
            const userEmail = sessionStorage.getItem("userEmail") || localStorage.getItem("userEmail") || "";
            const headers = {
                "x-user-id": localStorage.getItem("UserId") || "",
                "x-tenant-schema": "Amplo111",
                "x-user-email": userEmail,
            };

            const res = await api.get("/getAllCompanies", { headers });
            let comps = res.data.data || [];

            // FILTER by Project Selection Context if available
            if (userEmail && userEmail.toLowerCase() === "superadmin@amplo.com") {
                const projectSelectionStr = sessionStorage.getItem("projectSelection");
                if (projectSelectionStr) {
                    try {
                        const projectSelection = JSON.parse(projectSelectionStr);
                        if (projectSelection.client && projectSelection.client.company_id) {
                            const contextCompanyId = String(projectSelection.client.company_id);
                            const contextCompany = comps.find(c => String(c.company_id) === contextCompanyId);
                            if (contextCompany) {
                                comps = [contextCompany];
                            }
                        } else if (projectSelection.ampProject && projectSelection.ampProject.CompanyID) {
                            const contextCompanyId = String(projectSelection.ampProject.CompanyID);
                            const contextCompany = comps.find(c => String(c.company_id) === contextCompanyId);
                            if (contextCompany) {
                                comps = [contextCompany];
                            }
                        }
                    } catch (e) {
                        console.error("Error parsing project selection:", e);
                    }
                }
            }

            setCompanies(comps);
            if (comps.length > 0) {
                setSelectedCompanyId(comps[0].company_id);
            }

            setEntities(
                comps.flatMap(c =>
                    (c.entity_level_industry_mappings || []).map(e => ({
                        entity_id: e.entity_id,
                        entity_name: e.entity_name,
                        company_id: c.company_id,
                    }))
                )
            );

            setRawOperatingUnits(
                comps.flatMap(c => ({
                    company_id: c.company_id,
                    company_level_operating_units: c.company_level_operating_units || [],
                    entity_level_operating_units: c.entity_level_operating_units || [],
                }))
            );
        }

        fetchMeta();
    }, []);

    useEffect(() => {
        if (!selectedCompanyId) {
            setOperatingUnits([]);
            return;
        }

        const companyData = rawOperatingUnits.find(c => c.company_id === selectedCompanyId);
        if (!companyData) {
            setOperatingUnits([]);
            return;
        }

        let units = [];

        if (selectedEntityId) {
            // Filter only entity-level units
            units = companyData.entity_level_operating_units
                .filter(u => u.entity_id === selectedEntityId)
                .map(u => ({
                    unit_id: u.unit_id,
                    name: u.name,
                    company_id: selectedCompanyId,
                }));
        } else {
            // Filter only company-level units
            units = companyData.company_level_operating_units.map(u => ({
                unit_id: u.unit_id,
                name: u.name,
                company_id: selectedCompanyId,
            }));
        }

        setOperatingUnits(units);
        setSelectedUnitId(""); // Reset selected unit when filters change
    }, [rawOperatingUnits, selectedCompanyId, selectedEntityId]);


    // Fetch categoryList when filters change
    useEffect(() => {
        async function fetchTemplates() {
            dispatch(startLoader());
            const params = {
                companyId: selectedCompanyId || undefined,
                entityId: selectedEntityId || undefined,
                unitId: selectedUnitId || undefined,
            };

            const res = await api.get("/getDataTemplatesForReporting", {
                headers: getDefaultHeaders(),
                params,
            });

            const list = (res.data.categories || []).map((t) => ({
                label: t.name,
                categoryId: t.categoryId,
                subTypes: (t.subTypes || []).map((s) => ({
                    label: s.type,
                    value: s.type,
                    fileIds: s.fileIds || [],
                })),
            }));

            setCategoryList(list);
            setSelectedCategory(null);
            setChartData([]);
            setUnitLabel("");
            dispatch(stopLoader());
        }
        fetchTemplates();
    }, [selectedCompanyId, selectedEntityId, selectedUnitId]);

    // Fetch the actual data-rows for the selected template
    useEffect(() => {
        async function fetchEmissionData() {
            dispatch(startLoader());
            setChartData([]);
            setPieChartSummary([]);
            setunitConsumptionSummary([])
            setUnitLabel("");

            if (!selectedCategory || !selectedSubtype) {
                dispatch(stopLoader());
                return;
            }

            const fileIds = selectedSubtype?.fileIds || [];
            const fileidParam = fileIds.length ? fileIds.join(",") : undefined;

            const params = {
                companyId: selectedCompanyId || undefined,
                entityId: selectedEntityId || undefined,
                unitId: selectedUnitId || undefined,
                fileIds: fileidParam,
                category: selectedCategory.label,
                subType: selectedSubtype.label,
            };

            try {
                const res = await api.get(`/carbon-emission/calculate`, {
                    headers: getDefaultHeaders(),
                    params,
                });

                const rows = Array.isArray(res.data?.emissionData)
                    ? res.data.emissionData
                    : [];

                const unit = res.data?.unit || "";
                setUnitLabel(unit);

                const chart = rows
                    .filter(r => r.year && !isNaN(r.year))
                    .map(r => ({
                        year: Number(r.year),
                        totalCO2Emission: Number(r.totalEmission) || 0,
                        source: r.type,
                        subType: r.subType,
                        unit: r.unit
                    }));


                setChartData(chart);
                setPieChartSummary(res.data?.pieChartSummary || []);
                setunitConsumptionSummary(res.data?.unitConsumptionSummary || [])
                dispatch(stopLoader());
            } catch (err) {
                console.error("Failed to fetch carbon emission data:", err);
                dispatch(stopLoader());
            }
        }
        console.log("chartData", chartData);
        fetchEmissionData();
    }, [
        selectedCategory,
        selectedSubtype,
        selectedCompanyId,
        selectedEntityId,
        selectedUnitId,
    ]);


    useEffect(() => {
        async function fetchDashboardMetrics() {
            try {
                dispatch(startLoader());
                const params = {
                    companyId: selectedCompanyId || undefined,
                }
                const res = await api.get("/getDashboardMetrics", {
                    headers: getDefaultHeaders(), params
                });

                const data = res.data;

                if (data) {
                    setSummary([
                        { label: "Files Ingested", value: data.totalFiles },
                        { label: "Data Categories", value: data.totalCategories },
                        { label: "Data Templates", value: data.totalTemplates },
                        { label: "Completion for GRI", value: "31%" }, // Replace with actual if available
                    ]);

                    setTopPerformers(
                        (data.topPerformers || []).map(p => ({
                            name: p.name,
                            files: p.files,
                        }))
                    );
                    dispatch(stopLoader());
                }

            } catch (err) {
                dispatch(stopLoader());
                console.error("Failed to fetch dashboard metrics:", err);
            }
        }

        fetchDashboardMetrics();
    }, [selectedCompanyId]);

    // Select options
    const levelOptions = [
        { label: "Company", value: "company" },
        { label: "Entity", value: "entity" },
        { label: "Operating Unit", value: "unit" },
    ];
    const yearOptions = [
        { label: "FY2025", value: "FY2025" },
        { label: "FY2024", value: "FY2024" },
    ];

    const handleExportClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const exportAsPNG = async () => {
        handleClose();
        const canvas = await html2canvas(chartRef.current);
        const link = document.createElement("a");
        link.download = "chart.png";
        link.href = canvas.toDataURL("image/png");
        link.click();
    };

    const exportAsExcel = () => {
        handleClose();
        const formattedData = chartData.map(d => ({
            Date: new Date(d.date).toLocaleDateString('en-US', {
                month: 'short',
                year: 'numeric',
            }), // e.g., Aug 2025
            Consumption: d.consumption,
        }));

        const worksheet = XLSX.utils.json_to_sheet(formattedData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Chart Data");
        XLSX.writeFile(workbook, "chart-data.xlsx");
    };

    const handleCategoryChange = (opt) => {
        setSelectedCategory(opt);
        setSelectedSubtype(null);
        setChartData([]);
        setPieChartSummary([]);
        setunitConsumptionSummary([])

        const match = categoryList.find((cat) => cat.categoryId === opt.categoryId);
        const mappedSubtypes = (match?.subTypes || []).map((s) => ({
            label: s.label,
            value: s.label,
            fileIds: s.fileIds,
        }));

        setSubtypes(mappedSubtypes);
    };

    const handleSubtypeChange = (opt) => {
        setSelectedSubtype(opt);
    };

    const fetchEFData = async () => {
        try {
            const headers = getDefaultHeaders();
            const res = await api.get('/master-table-columns', { headers });
            const response = res.data;
            setEfData({
                fuel: response.fuel_master || [],
                electricity: response.electricity_grid_master || []
            });
        } catch (err) {
            console.error("Failed to fetch emission factor data:", err);
        }
    };

    useEffect(() => {
        if (showEFModal) {
            fetchEFData();
        }
    }, [showEFModal]);


    return (
        <div className={styles.fileUploadCont}>
            <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setShowEFModal(true)}
                    startIcon={<InfoOutlinedIcon />}
                    style={{ textTransform: "none", fontWeight: 500 }}
                >
                    Emission Factor Library
                </Button>
            </Box>
            <Box mb={4}>
                <Grid container spacing={2}>
                    {/* Level */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography><b>Select Level</b></Typography>
                        <Select
                            options={levelOptions}
                            value={levelOptions.find(o => o.value === selectedUploadLevel)}
                            onChange={opt => setSelectedUploadLevel(opt.value)}
                        />
                    </Grid>

                    {/* Company */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography><b>Select Company</b></Typography>
                        <Select
                            options={companies.map(c => ({ label: c.name, value: c.company_id }))}
                            value={companies
                                .map(c => ({ label: c.name, value: c.company_id }))
                                .find(o => o.value === selectedCompanyId)}
                            onChange={opt => setSelectedCompanyId(opt.value)}
                        />
                    </Grid>

                    {/* Entity */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography><b>Select Entity</b></Typography>
                        <Select
                            options={entities
                                .filter(e => e.company_id === selectedCompanyId)
                                .map(e => ({ label: e.entity_name, value: e.entity_id }))}
                            value={entities
                                .map(e => ({ label: e.entity_name, value: e.entity_id }))
                                .find(o => o.value === selectedEntityId)}
                            onChange={opt => {
                                setSelectedEntityId(opt.value);
                                setSelectedUnitId("");
                                setSelectedCategory(null);
                                setChartData([]);
                                setPieChartSummary([]);
                                setCategoryList([]);
                                setSubtypes([])
                                setUnitLabel("");
                            }}
                        />
                    </Grid>

                    {/* Operating Unit */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography><b>Select Operating Unit</b></Typography>
                        <Select
                            options={operatingUnits
                                .filter(u => u.company_id === selectedCompanyId)
                                .map(u => ({ label: u.name, value: u.unit_id }))}
                            value={
                                selectedUnitId
                                    ? operatingUnits
                                        .map(u => ({ label: u.name, value: u.unit_id }))
                                        .find(o => o.value === selectedUnitId)
                                    : null
                            }
                            onChange={opt => setSelectedUnitId(opt.value)}
                        />
                    </Grid>

                    {/* Templates */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Typography><b>Data Categories</b></Typography>
                        <Select
                            options={categoryList}
                            value={selectedCategory}
                            placeholder="Select Category"
                            onChange={handleCategoryChange}
                        />
                    </Grid>

                    {subtypes.length > 0 && (
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography><b>Subtype</b></Typography>
                            <Select
                                placeholder="Select Subtype"
                                options={subtypes}
                                value={selectedSubtype}
                                onChange={handleSubtypeChange}
                            />
                        </Grid>
                    )}

                    {/* Years */}
                    {/* <Grid item xs={12} sm={6} md={3}>
                        <Typography><b>Reporting Year</b></Typography>
                        <Select
                            options={yearOptions}
                            isMulti
                            value={years}
                            onChange={setYears}
                        />
                    </Grid> */}

                </Grid>
            </Box>
            <Card style={{ marginBottom: 24 }}>
                <CardContent>
                    {/* Chart Header */}
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6">
                            {selectedCategory ? `${selectedCategory.label}` : ""}
                        </Typography>
                        <Box>
                            {unitLabel && (
                                <Chip
                                    label={`Unit: ${unitLabel}`}
                                    style={{ background: "#e3f2fd", color: "#1976d2", marginRight: 8 }}
                                />
                            )}
                            {/* {chartData.length > 0 && (
                                <>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<ExportIcon />}
                                        onClick={handleExportClick}
                                    >
                                        Export
                                    </Button>

                                    <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
                                        <MenuItem onClick={exportAsPNG}>Download as PNG</MenuItem>
                                        <MenuItem onClick={exportAsExcel}>Download as Excel</MenuItem>
                                    </Menu>
                                </>
                            )} */}

                        </Box>
                    </Box>
                    {chartData.length > 0 ? (
                        <div ref={chartRef}>
                            <ResponsiveContainer width="100%" height={350}>
                                <BarChart
                                    data={chartData.filter(d => d.totalCO2Emission > 0)}
                                    margin={{ top: 20, right: 10, left: 10, bottom: 60 }}
                                    barCategoryGap="30%"
                                >
                                    <XAxis
                                        dataKey="year"
                                        type="category"
                                        tickFormatter={(tick) => tick.toString()}
                                        tick={{ angle: -45, textAnchor: 'end' }}
                                        interval={0}
                                        padding={{ left: 20, right: 0 }}
                                        label={{ value: 'Year', position: 'bottom', offset: 20 }}
                                    />
                                    <YAxis
                                        label={{ value: unitLabel || 'tCO₂', angle: -90, position: 'insideLeft' }}
                                    />
                                    <RechartsTooltip
                                        labelFormatter={(label) => `Year: ${label}`}
                                        formatter={(value) => [`${value.toFixed(2)} ${unitLabel || 'tCO₂'}`, 'Total Emission']}
                                    />
                                    <Bar dataKey="totalCO2Emission" barSize={20} radius={[4, 4, 0, 0]} name="Total CO₂ Emission">
                                        {chartData.map((entry, index) => (
                                            <Cell
                                                key={`bar-${index}`}
                                                fill={barColors[index % barColors.length]}
                                            />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    ) : (
                        <Box
                            height={300}
                            display="flex"
                            flexDirection="column"
                            justifyContent="center"
                            alignItems="center"
                            color="textSecondary"
                        >
                            <img
                                src={EmptyChartImage}
                                alt="No Data"
                                height={100}
                                style={{ marginBottom: 16 }}
                            />
                            <Typography variant="subtitle1">No data available to display.</Typography>
                            <Typography variant="body2">
                                Please select a category and subtype to view the chart.
                            </Typography>
                        </Box>
                    )}

                    {unitConsumptionSummary.length > 0 && (
                        <Box mt={4}>
                            <Typography variant="h6" gutterBottom>
                                Operating Unit Wise Emissions Summary
                            </Typography>

                            <Grid container spacing={4}>
                                {unitConsumptionSummary.map((yearGroup) => (
                                    <Grid item xs={12} md={6} key={yearGroup.year}>
                                        <Card>
                                            <CardContent>
                                                <Typography variant="subtitle1" style={{ marginBottom: 8 }}>
                                                    FY{yearGroup.year}
                                                </Typography>

                                                <ResponsiveContainer width="100%" height={300}>
                                                    <PieChart>
                                                        <Pie
                                                            data={yearGroup.data}
                                                            dataKey="totalEmission"
                                                            nameKey="name"
                                                            cx="50%"
                                                            cy="50%"
                                                            outerRadius={100}
                                                            labelLine={true}
                                                            label={({ name, percent }) =>
                                                                `${name} (${(percent * 100).toFixed(1)}%)`
                                                            }
                                                        >
                                                            {yearGroup.data.map((entry, index) => (
                                                                <Cell
                                                                    key={`cell-${index}`}
                                                                    fill={pieColors[index % pieColors.length]}
                                                                />
                                                            ))}
                                                        </Pie>
                                                        <Legend layout="horizontal" verticalAlign="bottom" align="center" />
                                                    </PieChart>
                                                </ResponsiveContainer>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    )}
                    {unitConsumptionSummary.length > 0 && (
                        <Box mt={4}>
                            <Typography variant="h6" style={{ marginBottom: 16 }}>
                                Emission Contribution
                            </Typography>

                            <Grid container spacing={4}>
                                {pieChartSummary.map((yearData, idx) => (
                                    <Grid item xs={12} md={6} key={yearData.year}>
                                        <Card>
                                            <CardContent>
                                                <Typography variant="subtitle1" style={{ marginBottom: 8 }}>
                                                    FY{yearData.year}
                                                </Typography>

                                                <div style={{ overflow: 'visible' }}>
                                                    <ResponsiveContainer width="100%" height={350}>
                                                        <PieChart>
                                                            <Pie
                                                                data={yearData.data}
                                                                dataKey="totalEmission"
                                                                nameKey="name"
                                                                cx="50%"
                                                                cy="50%"
                                                                outerRadius={100}
                                                                labelLine={true}
                                                                label={({ name, percent }) =>
                                                                    `${name} (${(percent * 100).toFixed(1)}%)`
                                                                }
                                                            >
                                                                {yearData.data.map((entry, index) => (
                                                                    <Cell key={`cell-${index}`} fill={pieColors[index % pieColors.length]} />
                                                                ))}
                                                            </Pie>
                                                            <Legend layout="horizontal" verticalAlign="bottom" align="center" />
                                                        </PieChart>
                                                    </ResponsiveContainer>
                                                </div>

                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    )}

                </CardContent>
            </Card>

            <Dialog open={showEFModal} onClose={() => setShowEFModal(false)} maxWidth="lg" fullWidth>
                <DialogTitle>Emission Factor Library</DialogTitle>
                <DialogContent>
                    <Tabs value={efTab} onChange={(_, newVal) => setEfTab(newVal)}>
                        <Tab label="Fuel" />
                        <Tab label="Electricity" />
                    </Tabs>
                    {efTab === 0 && (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    {efData.fuel.length > 0 && Object.keys(efData.fuel[0]).map(key => (
                                        <TableCell key={key}>{key}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {efData.fuel.map((row, idx) => (
                                    <TableRow key={idx}>
                                        {Object.values(row).map((val, i) => (
                                            <TableCell key={i}>{val?.toString()}</TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                    {efTab === 1 && (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    {efData.electricity.length > 0 && Object.keys(efData.electricity[0]).map(key => (
                                        <TableCell key={key}>{key}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {efData.electricity.map((row, idx) => (
                                    <TableRow key={idx}>
                                        {Object.values(row).map((val, i) => (
                                            <TableCell key={i}>{val?.toString()}</TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </DialogContent>
            </Dialog>
        </div>

    );
};

export default CarbonEmission;
